<template>
  <form
    ref="formRef"
    class="form"
    method="POST"
    :action="formAction"
    :disabled="isLoading"
    @submit.prevent="submitForm"
  >
    <Title>Add a Person</Title>

    <div class="form-labels">
      <label class="form-label">
        <input
          v-model="newPersonType"
          type="radio"
          name="entity_type"
          value="prospect"
        >

        <Badge status="info">Prospect</Badge>
      </label>

      <label class="form-label">
        <input
          v-model="newPersonType"
          type="radio"
          name="entity_type"
          value="client"
        >

        <Badge status="success">Client</Badge>
      </label>
    </div>
    
    <!-- server‐side general errors -->
    <div class="error-container" v-if="errors.general && errors.general.length">
      <Callout
        v-for="(error, index) in errors.general"
        :key="index"
        status="danger"
      >
        {{ error.message }}
      </Callout>
    </div>

    <input
      id="id_first_name"
      v-model="firstName"
      class="stk-input"
      type="text"
      placeholder="First name"
      name="first_name"
      maxlength="30"
      @input="handleInput"
      required
    >

    <input
      id="id_last_name"
      v-model="lastName"
      class="stk-input"
      type="text"
      placeholder="Last name"
      name="last_name"
      maxlength="150"
      @input="handleInput"
    >

    <input
      id="id_email"
      v-model="emailAddress"
      class="stk-input"
      type="email"
      placeholder="Email"
      name="email"
      maxlength="150"
      @input="handleInput"
      required
    />
    <!-- client‐side email error -->
    <p
      v-if="emailAddress && !emailIsValid"
      class="tw-text-sm tw-text-red-600"
    >
      Please enter a valid email address.
    </p>

    <Buttons>
      <Button
        variant="primary"
        :disabled="!isValidForm"
        :loading="isLoading"
      >
        Add new Person
      </Button>

      <Button @click="closeModal" type="button">
        Close
      </Button>
    </Buttons>
  </form>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';

import {
  Badge,
  Button,
  Buttons,
  Callout,
  Title,
} from '@stakeui';

const isLoading = ref(false);
const newPersonType = ref('prospect');
const errors = ref({ general: [] });
const firstName = ref('');
const lastName = ref('');
const emailAddress = ref('');
const formRef = ref(null);
const instance = getCurrentInstance();

const emit = defineEmits(['onClose', 'onAdd']);

const formAction = computed(() => {
  return newPersonType.value === 'prospect' ?
    '/advisordashboard/prospect-create/' :
    '/advisordashboard/shareowner-create/';
});

// simple RFC‑ish regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailIsValid = computed(() => emailRegex.test(emailAddress.value));

const isValidForm = computed(() =>
  (firstName.value !== '' || lastName.value !== '') &&
  emailAddress.value !== '' &&
  emailIsValid.value
);

const getCSRF = computed(() => {
  const selector = '[name="csrfmiddlewaretoken"]';
  return document.querySelector(selector)?.value;
});

const closeModal = () => {
  emit('onClose', false);
};

const handleInput = () => {
  errors.value = { general: [] };
};

const resetForm = () => {
  firstName.value = '';
  lastName.value = '';
  emailAddress.value = '';
  errors.value = { general: [] };
};

const normalizeErrors = (data) => {
  // Initialize normalized errors structure
  const normalizedErrors = { general: [] };
  
  // Handle different error response formats
  if (data.errors && typeof data.errors === 'object') {
    // Check if errors is our expected format with general field
    if (data.errors.general && Array.isArray(data.errors.general)) {
      // Only take the first error message
      if (data.errors.general.length > 0) {
        normalizedErrors.general = [data.errors.general[0]];
      }
    } else {
      // Handle Django form.errors.get_json_data() format
      // Just get the first error from any field
      const entries = Object.entries(data.errors);
      if (entries.length > 0) {
        const [field, fieldErrors] = entries[0];
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
          normalizedErrors.general.push({
            message: fieldErrors[0].message
          });
        }
      }
    }
  } else if (data.error) {
    // Format: { success: false, error: 'error message' } (from prospect_create)
    normalizedErrors.general.push({ message: data.error });
  } else if (typeof data.errors === 'string') {
    // Format: { success: false, errors: 'error message' }
    normalizedErrors.general.push({ message: data.errors });
  }
  
  return normalizedErrors;
};

const submitForm = async () => {
  if (isLoading.value) {
    return;
  }
  if (!emailIsValid.value) {
    errors.value = {
      general: [{ message: 'Please enter a valid email address' }]
    };
    return;
  }

  isLoading.value = true;
  const formData = new FormData(formRef.value);
  const postData = new URLSearchParams(formData);

  try {
    const res = await fetch(formAction.value, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRFToken': getCSRF.value,
      },
      body: postData,
    });

    const data = await res.json();
    
    if (res.ok && data.success) {
      // Determine entity type for success message
      const entityType = newPersonType.value === 'prospect' ? 'prospect' : 'client';
      
      const created = data.prospect || data.shareowner || null;
      const externalId = created?.entity_external_id || created?.id || null;
      const createdName = created?.name || `${created?.first_name || ''} ${created?.last_name || ''}`.trim();
      const createdEmail = created?.email || emailAddress.value;

      instance?.proxy?.$buefy.toast.open({
        message: `Successfully added new ${entityType}`,
        type: 'is-success',
        duration: 5000,
      });
      resetForm();
      emit('onAdd', {
        external_id: externalId,
        name: createdName,
        email: createdEmail,
        type: entityType,
      });
      return;
    } else {
      console.error('response data', data);
      errors.value = normalizeErrors(data);
    }
  } catch (error) {
    console.error('Error creating entity:', error);
    errors.value = { 
      general: [{ message: 'Failed to create entity. Please try again later.' }] 
    };
  } finally {
    isLoading.value = false;
    closeModal();
  }
};
</script>

<style scoped>
.form {
  width: 30em;
  padding: 0.75em 1em;
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.form-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
}

.form-label {
  cursor: pointer;
}
.error-container {
  position: sticky;
  top: 0;
  background: white;   /* or whatever your modal's bg is */
  padding-bottom: 0.75em;
  z-index: 10;
}
</style>
